import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash, Key } from "@phosphor-icons/react";

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "admin" });
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/admin/users", form); toast.success("User created"); setOpen(false); setForm({ email: "", name: "", password: "", role: "admin" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openReset = (u) => {
    setResetTarget(u);
    setNewPassword("");
    setResetOpen(true);
  };
  const submitReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.put(`/admin/users/${resetTarget.id}/password`, { password: newPassword });
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetOpen(false);
      setResetTarget(null);
      setNewPassword("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Admin Users"
        subtitle="Manage who has access to the admin console."
        actions={
          <Button onClick={() => setOpen(true)} data-testid="add-user-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
            <Plus size={16} className="mr-2" />New user
          </Button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8F6] text-left text-[10px] tracking-[0.2em] uppercase text-[#4B5563]">
              <tr>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Role</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {items.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-3 font-medium text-[#1F2937]">{u.name}</td>
                  <td className="px-6 py-3 text-[#4B5563]">{u.email}</td>
                  <td className="px-6 py-3"><span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-[#C2410C]/10 text-[#C2410C]">{u.role}</span></td>
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={() => openReset(u)}
                      data-testid={`reset-pwd-${u.id}`}
                      className="text-[#4B5563] hover:text-[#C2410C] mr-3"
                      title="Reset password"
                    ><Key size={16} /></button>
                    {u.id !== user?.id && (
                      <button onClick={() => remove(u.id)} data-testid={`del-${u.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                    )}
                    {u.id === user?.id && <span className="text-xs text-[#4B5563]">You</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>New admin user</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="u-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="u-email" /></div>
            <div className="space-y-2"><Label>Temporary password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="u-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-user" className="bg-[#C2410C] hover:bg-[#9A3412]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle>Reset password</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {resetTarget && (
              <p className="text-sm text-[#4B5563]">
                Setting a new password for <b className="text-[#1F2937]">{resetTarget.email}</b>. The user will need to sign in with this password next.
              </p>
            )}
            <div className="space-y-2">
              <Label>New password</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                data-testid="reset-password-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button onClick={submitReset} data-testid="reset-password-submit" className="bg-[#C2410C] hover:bg-[#9A3412]">Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
